import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Chip,
  Paper,
  Divider,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Fade,
  Stack,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import toast from 'react-hot-toast';
import { isToday, isYesterday, isThisWeek, isThisMonth, parseISO } from 'date-fns';
import PropTypes from 'prop-types';

import { notesApi } from 'src/api/notesApi';
import { queryApi } from 'src/api/queryApi';
import { expertApi } from 'src/api/expertApi';
import useSWR from 'swr';
import useFormatDate from 'src/hooks/useFormatDate';
import useUsers from 'src/hooks/useUsers';
import { useDecodedToken } from 'src/hooks/useDecodedToken';

const TWO_MINUTES = 2 * 60 * 1000;

const expertsFetcher = () => expertApi.getExperts();

const QueryNotesTab = ({ queryID, expert, pitch }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showReturnHint, setShowReturnHint] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [needsChangesDialogOpen, setNeedsChangesDialogOpen] = useState(false);
  const [approvalNote, setApprovalNote] = useState('');
  const [changesNote, setChangesNote] = useState('');
  const { users } = useUsers();
  const { decodedToken, isExpert } = useDecodedToken();

  const { data: experts } = useSWR('experts', expertsFetcher);

  // Memoize the dates array to prevent infinite re-renders
  const submitDates = useMemo(() => notes.map((note) => note.submitDate), [notes]);
  const formattedDates = useFormatDate(submitDates);

  const fetchNotes = useCallback(async () => {
    if (!queryID) {
      toast.error('No queryID provided');
      return;
    }
    try {
      if (isExpert) {
        const response = await notesApi.fetchNotes(Number(queryID), expert?.id);
        const filteredNotes = response.filter((note) => note.userType === 1);
        setNotes(filteredNotes.sort((a, b) => parseISO(b.submitDate) - parseISO(a.submitDate)));
      } else {
        const response = await notesApi.fetchNotes(Number(queryID));
        setNotes(response.sort((a, b) => parseISO(b.submitDate) - parseISO(a.submitDate)));
      }
    } catch (error) {
      toast.error('Error fetching notes:', error);
    }
  }, [queryID, expert?.id, isExpert]);

  useEffect(() => {
    if (queryID) {
      fetchNotes();
    }
  }, [fetchNotes, queryID]);

  const handleAddNote = async () => {
    if (!newNote.trim() || !queryID) return;

    try {
      if (isExpert) {
        await notesApi.addNote(Number(queryID), newNote.trim(), expert?.id);
      } else {
        await notesApi.addNote(Number(queryID), newNote.trim());
      }
      setNewNote('');
      fetchNotes();
    } catch (error) {
      toast.error('Error adding note:', error);
    }
  };

  const handleEditNote = async () => {
    if (!editingNote || !editingNote.content.trim()) return;

    try {
      if (isExpert) {
        await notesApi.editNote(editingNote.id, editingNote.content.trim(), expert?.id);
      } else {
        await notesApi.editNote(editingNote.id, editingNote.content.trim());
      }
      setEditingNote(null);
      fetchNotes();
    } catch (error) {
      toast.error('Error editing note');
    }
  };

  const handleDeleteClick = (note) => {
    setSelectedNote(note);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedNote?.id) return;
    try {
      if (isExpert) {
        await notesApi.deleteNote(selectedNote.id, expert?.id);
      } else {
        await notesApi.deleteNote(selectedNote.id);
      }
      setDeleteDialogOpen(false);
      setSelectedNote(null);
      fetchNotes();
      toast.success('Note deleted successfully');
    } catch (error) {
      toast.error('Error deleting note');
    }
  };

  const handleStartEdit = (note) => {
    setEditingNote(note);
  };

  const getUserInfo = (userId, userType) => {
    if (userType === 1) {
      // Look up in experts array
      const expertUser = experts?.find((e) => e.id === userId);
      return {
        name: expertUser ? expertUser.name : 'Unknown Expert',
        avatar: expertUser?.imageURL || '',
        initials: expertUser
          ? expertUser.name
              .split(' ')
              .map((n) => n[0])
              .join('')
              .substring(0, 2)
          : 'E',
      };
    } else {
      // Look up in users array (default behavior)
      const user = users?.find((u) => u.id === userId);
      return {
        name: user ? `${user.firstName} ${user.lastName}` : 'Unknown User',
        avatar: user?.avatar || '',
        initials: user ? `${user.firstName[0]}${user.lastName[0]}` : 'U',
      };
    }
  };

  const handleKeyDown = (event) => {
    // Show return hint when user types
    setShowReturnHint(event.target.value.length > 0);

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (newNote.trim()) {
        handleAddNote();
      }
    } else if (event.key === 'Escape' && editingNote) {
      setEditingNote(null);
    }
  };

  const groupNotesByDate = (notesList) => {
    return notesList.reduce((groups, note) => {
      const date = parseISO(note.submitDate);
      let group;

      if (isToday(date)) group = 'Today';
      else if (isYesterday(date)) group = 'Yesterday';
      else if (isThisWeek(date)) group = 'This Week';
      else if (isThisMonth(date)) group = 'This Month';
      else group = 'Earlier';

      if (!groups[group]) groups[group] = [];
      groups[group].push(note);
      return groups;
    }, {});
  };

  const groupedNotes = groupNotesByDate(notes);
  const orderedGroups = ['Today', 'Yesterday', 'This Week', 'This Month', 'Earlier'];

  const handleApprove = async () => {
    try {
      const content = approvalNote.trim() ? `Approved: ${approvalNote.trim()}` : 'Approved';

      // Add the note
      await notesApi.addNote(Number(queryID), content, expert?.id);

      // Update the query status to clientApproved
      const response = await queryApi.updateQuery(Number(queryID), 'clientApproved');
      if (response.status === 200) {
        setApprovalNote('');
        setApproveDialogOpen(false);
        fetchNotes();
        toast.success('Query approved successfully');
      } else {
        toast.error('Error approving query');
      }
    } catch (error) {
      toast.error('Error approving query');
    }
  };

  const handleNeedsChanges = async () => {
    if (!changesNote.trim()) return;

    try {
      const content = `Needs changes: ${changesNote.trim()}`;

      // Add the note
      await notesApi.addNote(Number(queryID), content, expert?.id);

      // Update the query status to clientNeedsEdits
      const response = await queryApi.updateQuery(Number(queryID), 'clientNeedsEdits');
      if (response.status === 200) {
        setChangesNote('');
        setNeedsChangesDialogOpen(false);
        fetchNotes();
        toast.success('Feedback sent successfully');
      } else {
        toast.error('Error sending feedback');
      }
    } catch (error) {
      toast.error('Error sending feedback');
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
      {isExpert && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Pitch
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              whiteSpace: 'pre-wrap',
            }}
          >
            {pitch}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() => setApproveDialogOpen(true)}
              sx={{
                textTransform: 'none',
                flex: 1,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  opacity: 0.8,
                },
              }}
            >
              Approve
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<WarningAmberIcon />}
              onClick={() => setNeedsChangesDialogOpen(true)}
              sx={{
                textTransform: 'none',
                flex: 1,
                '&:hover': {
                  backgroundColor: 'warning.main',
                  opacity: 0.8,
                },
              }}
            >
              Needs changes
            </Button>
          </Stack>
        </Box>
      )}

      <Box
        sx={{
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          zIndex: 1,
          pb: 2,
          mb: 0,
        }}
      >
        <TextField
          fullWidth
          placeholder="Write a note..."
          variant="outlined"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              transition: 'all 0.2s ease-in-out',
              '&:hover, &.Mui-focused': {
                bgcolor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              },
            },
            '& .MuiOutlinedInput-input': {
              p: 1.25,
              fontSize: '0.875rem',
              '&::placeholder': {
                opacity: 0.8,
              },
              whiteSpace: 'pre-wrap',
            },
          }}
          multiline
          minRows={1}
          maxRows={4}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 1, color: 'text.secondary' }}>
                <ChatBubbleOutlineIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Fade in={showReturnHint}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, color: 'text.secondary' }}>
                    <KeyboardReturnIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="caption">Send</Typography>
                  </Box>
                </Fade>
                <Button
                  variant="contained"
                  onClick={handleAddNote}
                  disabled={!newNote.trim()}
                  size="small"
                  sx={{
                    minWidth: 'unset',
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    mr: 0.5,
                    opacity: newNote.trim() ? 1 : 0.5,
                    transition: 'opacity 0.2s ease-in-out',
                  }}
                >
                  <SendIcon fontSize="small" />
                </Button>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {notes.length > 0 && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
            {orderedGroups.map((group) => {
              if (!groupedNotes[group]?.length) return null;

              return (
                <Box key={group} sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'text.primary',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      mb: 2,
                    }}
                  >
                    {group}
                  </Typography>
                  {groupedNotes[group].map((note, index) => {
                    const userInfo = getUserInfo(note.userID, note.userType);
                    const prevNote = index > 0 ? groupedNotes[group][index - 1] : null;
                    const nextNote = index < groupedNotes[group].length - 1 ? groupedNotes[group][index + 1] : null;

                    // Check if messages are from different users or have a time gap
                    const timeDiffWithPrev = prevNote ? parseISO(prevNote.submitDate).getTime() - parseISO(note.submitDate).getTime() : TWO_MINUTES;
                    const timeDiffWithNext = nextNote ? parseISO(note.submitDate).getTime() - parseISO(nextNote.submitDate).getTime() : TWO_MINUTES;

                    const isFirstInSequence = !prevNote || prevNote.userID !== note.userID || timeDiffWithPrev >= TWO_MINUTES;

                    const isLastInSequence = !nextNote || nextNote.userID !== note.userID || timeDiffWithNext >= TWO_MINUTES;

                    return (
                      <Box
                        key={note.id}
                        sx={{
                          mb: isLastInSequence ? 2 : 0.75,
                          position: 'relative',
                          '&:hover .message-actions': {
                            opacity: 1,
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Box sx={{ width: 32, alignSelf: isFirstInSequence ? 'flex-start' : 'flex-start', mt: isFirstInSequence ? 0.5 : 0 }}>
                            {isFirstInSequence && (
                              <Avatar
                                src={userInfo.avatar}
                                alt={userInfo.name}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: 'primary.main',
                                  fontSize: '0.875rem',
                                }}
                              >
                                {userInfo.initials}
                              </Avatar>
                            )}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                              {editingNote?.id === note.id ? (
                                <Box sx={{ flex: 1 }}>
                                  <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxRows={6}
                                    value={editingNote.content}
                                    onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
                                    onKeyDown={(e) => e.key === 'Escape' && setEditingNote(null)}
                                    variant="outlined"
                                    size="small"
                                    autoFocus
                                    sx={{
                                      bgcolor: 'white',
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        fontSize: '0.875rem',
                                      },
                                      '& .MuiOutlinedInput-input': {
                                        whiteSpace: 'pre-wrap',
                                      },
                                    }}
                                  />
                                  <Box sx={{ mt: 0.5, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Button size="small" onClick={() => setEditingNote(null)} sx={{ textTransform: 'none', fontSize: '0.8125rem' }}>
                                      Cancel
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={handleEditNote}
                                      sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
                                    >
                                      Save
                                    </Button>
                                  </Box>
                                </Box>
                              ) : (
                                <>
                                  <Typography
                                    variant="body2"
                                    component="pre"
                                    sx={{
                                      flex: 1,
                                      p: 1.5,
                                      bgcolor: note.userID === decodedToken?.userID ? '#409dff' : 'grey.100',
                                      color: note.userID === decodedToken?.userID ? 'white' : 'text.primary',
                                      borderRadius: 2,
                                      whiteSpace: 'pre-wrap',
                                      fontFamily: 'inherit',
                                      fontSize: '0.875rem',
                                      lineHeight: 1.5,
                                      maxWidth: 'calc(100% - 40px)',
                                      '& .MuiChip-root': {
                                        bgcolor: note.userID === decodedToken?.userID ? 'rgba(255,255,255,0.2)' : undefined,
                                        color: note.userID === decodedToken?.userID ? 'white' : undefined,
                                      },
                                    }}
                                  >
                                    {isFirstInSequence && (
                                      <Box
                                        component="span"
                                        sx={{
                                          fontWeight: 600,
                                          display: 'block',
                                          mb: 0.5,
                                          color: note.userID === decodedToken?.userID ? 'inherit' : 'text.primary',
                                        }}
                                      >
                                        {userInfo.name}
                                        {note.userType === 1 && (
                                          <Chip
                                            label="Expert"
                                            size="small"
                                            color={note.userID === decodedToken?.userID ? 'default' : 'success'}
                                            sx={{
                                              height: '16px',
                                              ml: 0.75,
                                              '& .MuiChip-label': {
                                                px: 0.75,
                                                fontSize: '0.625rem',
                                                fontWeight: 'medium',
                                                textTransform: 'uppercase',
                                              },
                                            }}
                                          />
                                        )}
                                      </Box>
                                    )}
                                    {note.content}
                                  </Typography>
                                  <Box
                                    className="message-actions"
                                    sx={{
                                      opacity: 0,
                                      transition: 'opacity 0.2s',
                                      display: note.userID === decodedToken?.userID ? 'flex' : 'none',
                                      flexDirection: 'column',
                                      gap: 0.5,
                                      position: 'absolute',
                                      right: 0,
                                      top: 0.5,
                                      zIndex: 2,
                                    }}
                                  >
                                    <Tooltip title="Edit" placement="left">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleStartEdit(note)}
                                        sx={{
                                          bgcolor: 'primary.main',
                                          color: 'white',
                                          opacity: 0.2,
                                          boxShadow: 1,
                                          width: 28,
                                          height: 28,
                                          '&:hover': {
                                            bgcolor: 'primary.main',
                                            boxShadow: 2,
                                            opacity: 1,
                                          },
                                        }}
                                      >
                                        <EditOutlinedIcon sx={{ fontSize: '0.9rem' }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete" placement="left">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteClick(note)}
                                        sx={{
                                          bgcolor: 'error.main',
                                          color: 'white',
                                          opacity: 0.2,
                                          boxShadow: 1,
                                          width: 28,
                                          height: 28,
                                          '&:hover': {
                                            bgcolor: 'error.main',
                                            boxShadow: 2,
                                            opacity: 1,
                                          },
                                        }}
                                      >
                                        <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </>
                              )}
                            </Box>
                            {(isLastInSequence || timeDiffWithNext >= TWO_MINUTES) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontSize: '0.75rem',
                                  display: 'block',
                                  mt: 0.5,
                                  ml: 0.5,
                                }}
                              >
                                {formattedDates[note.submitDate]}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Delete Note</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Are you sure you want to delete this note? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none', fontSize: '0.875rem' }}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error" sx={{ textTransform: 'none', fontSize: '0.875rem' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Approve Query</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Add any additional notes (optional):
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            placeholder="Enter any additional notes..."
            value={approvalNote}
            onChange={(e) => setApprovalNote(e.target.value)}
            variant="outlined"
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} sx={{ textTransform: 'none', fontSize: '0.875rem' }}>
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="primary"
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: 'primary.main',
                opacity: 0.8,
              },
            }}
            startIcon={<SendIcon />}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Needs Changes Dialog */}
      <Dialog
        open={needsChangesDialogOpen}
        onClose={() => setNeedsChangesDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Request Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide details about the changes needed:
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            placeholder="Describe the changes needed..."
            value={changesNote}
            onChange={(e) => setChangesNote(e.target.value)}
            variant="outlined"
            size="small"
            required
            error={needsChangesDialogOpen && !changesNote.trim()}
            helperText={needsChangesDialogOpen && !changesNote.trim() ? 'Notes are required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNeedsChangesDialogOpen(false)} sx={{ textTransform: 'none', fontSize: '0.875rem' }}>
            Cancel
          </Button>
          <Button
            onClick={handleNeedsChanges}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none', fontSize: '0.875rem' }}
            startIcon={<SendIcon />}
            disabled={!changesNote.trim()}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

QueryNotesTab.propTypes = {
  queryID: PropTypes.number.isRequired,
  expert: PropTypes.object,
  pitch: PropTypes.string,
};

export default QueryNotesTab;
